import React, { useEffect, useState } from "react";
import { apiInstanceFetch } from "../../../util/api";

// Faith daily-reflection quotes — admin CRUD. Self-contained (no redux):
// talks straight to the faithQuote endpoints via apiInstanceFetch. Rendered
// inside the admin dashboard at /admin/faithQuotes (behind admin auth).
const RELIGIONS = ["christianity", "islam", "judaism", "other"];
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

const C = {
  gold: "#F0C420",
  goldD: "#D4A129",
  ink: "#1A1614",
  cream: "#FFFDF7",
  paper: "#FAF7F0",
  muted: "#8A857F",
  line: "#E8E1D3",
};

const FaithQuote = () => {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ religion: "christianity", text: "", reference: "", order: 0 });
  const [msg, setMsg] = useState(null);

  const flash = (text, ok) => {
    setMsg({ text, ok });
    setTimeout(() => setMsg(null), 3000);
  };

  const load = async () => {
    try {
      const url = "faithQuote/list" + (filter ? `?religion=${filter}` : "");
      const j = await apiInstanceFetch.get(url);
      setRows(j?.data || []);
    } catch (e) {
      flash("Failed to load quotes", false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const resetForm = () => {
    setEditingId(null);
    setForm({ religion: "christianity", text: "", reference: "", order: 0 });
  };

  const save = async () => {
    if (!form.text.trim()) {
      flash("Quote text is required", false);
      return;
    }
    try {
      const body = { ...form, order: parseInt(form.order, 10) || 0 };
      const j = editingId
        ? await apiInstanceFetch.patch("faithQuote/update", { id: editingId, ...body })
        : await apiInstanceFetch.post("faithQuote/create", body);
      if (j?.status) {
        flash(editingId ? "Quote updated" : "Quote added", true);
        resetForm();
        load();
      } else {
        flash(j?.message || "Save failed", false);
      }
    } catch (e) {
      flash("Save failed", false);
    }
  };

  const edit = (q) => {
    setEditingId(q._id);
    setForm({ religion: q.religion, text: q.text, reference: q.reference || "", order: q.order || 0 });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const toggleActive = async (q) => {
    try {
      const j = await apiInstanceFetch.patch("faithQuote/update", { id: q._id, isActive: !q.isActive });
      if (j?.status) load();
    } catch (e) {
      flash("Failed", false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this quote?")) return;
    try {
      const j = await apiInstanceFetch.post("faithQuote/remove", { id });
      if (j?.status) {
        flash("Deleted", true);
        load();
      }
    } catch (e) {
      flash("Failed", false);
    }
  };

  const input = {
    width: "100%",
    padding: "10px 12px",
    border: `1px solid ${C.line}`,
    borderRadius: 10,
    background: "#fff",
    fontSize: 14,
    color: C.ink,
  };
  const label = { display: "block", fontSize: 12, color: C.muted, margin: "10px 0 4px", fontWeight: 600 };
  const btn = (bg, col) => ({
    cursor: "pointer",
    border: "none",
    borderRadius: 100,
    fontWeight: 700,
    fontSize: 13,
    padding: "9px 18px",
    background: bg,
    color: col,
  });
  const chip = (active) => ({
    padding: "7px 14px",
    borderRadius: 100,
    border: `1px solid ${active ? C.gold : C.line}`,
    background: active ? C.gold : "#fff",
    color: active ? C.ink : C.muted,
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 600,
  });

  return (
    <div style={{ background: C.paper, minHeight: "100vh", padding: "22px 18px" }}>
      <div style={{ maxWidth: 940, margin: "0 auto" }}>
        <h2 style={{ color: C.ink, margin: "0 0 16px", fontWeight: 700 }}>Faith Daily Quotes</h2>

        {msg && (
          <div
            style={{
              padding: "10px 14px",
              borderRadius: 10,
              marginBottom: 14,
              fontSize: 13,
              fontWeight: 600,
              background: msg.ok ? "#E7F6EC" : "#FCE8E6",
              color: msg.ok ? "#1B7A3D" : "#C0392B",
            }}
          >
            {msg.text}
          </div>
        )}

        <div style={{ background: C.cream, border: `1px solid ${C.line}`, borderRadius: 14, padding: 16, marginBottom: 18 }}>
          <h3 style={{ margin: "0 0 8px", fontSize: 15, color: C.ink }}>{editingId ? "Edit quote" : "Add a quote"}</h3>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 150 }}>
              <label style={label}>Religion</label>
              <select style={input} value={form.religion} onChange={(e) => setForm({ ...form, religion: e.target.value })}>
                {RELIGIONS.map((r) => (
                  <option key={r} value={r}>
                    {cap(r)}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ flex: 1, minWidth: 150 }}>
              <label style={label}>Reference (e.g. John 3:16 or author)</label>
              <input style={input} value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="John 3:16" />
            </div>
            <div style={{ width: 100 }}>
              <label style={label}>Order</label>
              <input style={input} type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} />
            </div>
          </div>
          <label style={label}>Quote text</label>
          <textarea style={{ ...input, minHeight: 64, resize: "vertical" }} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} placeholder="Type the reflection…" />
          <div style={{ marginTop: 14, display: "flex", gap: 10 }}>
            <button style={btn(C.gold, C.ink)} onClick={save}>
              {editingId ? "Save changes" : "Add quote"}
            </button>
            {editingId && (
              <button style={btn("transparent", C.ink)} onClick={resetForm}>
                Cancel edit
              </button>
            )}
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
          <div style={chip(filter === "")} onClick={() => setFilter("")}>
            All
          </div>
          {RELIGIONS.map((r) => (
            <div key={r} style={chip(filter === r)} onClick={() => setFilter(r)}>
              {cap(r)}
            </div>
          ))}
        </div>

        {rows.length === 0 ? (
          <div style={{ color: C.muted, textAlign: "center", padding: 30, fontSize: 13 }}>No quotes yet. Add one above.</div>
        ) : (
          rows.map((q) => (
            <div key={q._id} style={{ background: C.cream, border: `1px solid ${C.line}`, borderRadius: 12, padding: 14, marginBottom: 10 }}>
              <div style={{ fontSize: 14, lineHeight: 1.45, color: C.ink }}>{q.text}</div>
              {q.reference ? <div style={{ fontSize: 12, color: C.goldD, fontWeight: 700, marginTop: 4 }}>— {q.reference}</div> : null}
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 9px", borderRadius: 100, background: q.isActive ? "#F3EACF" : "#EEE", color: q.isActive ? "#7A5B00" : "#999", textTransform: "capitalize" }}>
                  {q.religion}
                </span>
                {!q.isActive && <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 9px", borderRadius: 100, background: "#EEE", color: "#999" }}>hidden</span>}
                <span style={{ flex: 1 }} />
                <button style={{ ...btn("transparent", C.ink), border: `1px solid ${C.line}`, padding: "6px 12px", fontSize: 12 }} onClick={() => edit(q)}>
                  Edit
                </button>
                <button style={{ ...btn("transparent", C.ink), border: `1px solid ${C.line}`, padding: "6px 12px", fontSize: 12 }} onClick={() => toggleActive(q)}>
                  {q.isActive ? "Hide" : "Show"}
                </button>
                <button style={{ ...btn("#FCE8E6", "#C0392B"), padding: "6px 12px", fontSize: 12 }} onClick={() => del(q._id)}>
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default FaithQuote;
